import { useMemo, FC, useState, useEffect, useCallback } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks/useRootStore';
import { useDebounce } from 'hooks/useDebounce';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { Button } from 'components/Core/Button';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { StatusCell } from 'components/Core/StatusCell';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { IAdminUser } from 'stores/users/types';
import { adminUsersTableTranslations } from './translations';
import { AdminUsersTableTransFields } from './types';
import styles from './adminUsersTable.module.scss';

const AdminUsersTable: FC = observer(() => {
  const {
    usersStore: { adminUsers, changeAdminUserStatus, setCurAdminUser },
    mainStore: { user },
    popUpStore: { openPopUp },
  } = useStore();

  const { t } = useMyTranslations<AdminUsersTableTransFields>([adminUsersTableTranslations.name]);

  const [search, setSearch] = useState('');
  const [filtred, setFiltred] = useState<IAdminUser[]>([]);

  const searchD = useDebounce(search);

  useEffect(() => {
    if (searchD)
      setFiltred(
        adminUsers?.filter(({ name, email, role }) =>
          [name, email, role].some((item) => item.toLowerCase().includes(searchD.toLowerCase()))
        ) || []
      );
    else setFiltred(adminUsers || []);
  }, [searchD, adminUsers]);

  const onEditClick = useCallback((item: IAdminUser) => {
    setCurAdminUser(item);
    openPopUp({ title: `${t('edit')} ${item.name}`, buttonLabel: t('save'), formName: 'admin-user' });
  }, []);

  const disabled = useMemo(() => {
    return user?.role !== 'super-admin';
  }, [user?.role]);

  const columns: Column<IAdminUser>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: { name },
          },
        }: CellProps<IAdminUser>) => <p className={styles.name}>{name}</p>,
        accessor: ({ name }) => name,
        width: 10,
      },
      {
        Header: t('email'),
        id: 'email',
        accessor: ({ email }) => `${email}`,
        width: 4,
      },
      {
        Header: t('role'),
        id: 'role',
        accessor: ({ role }) => `${role}`,
        width: 4,
      },
      {
        Header: t('usersCountryCodes'),
        id: 'usersCountryCodes',
        Cell: ({
          row: {
            original: { usersCountryCodes },
          },
        }: CellProps<IAdminUser>) => (
          <div className={styles.contacts}>
            {usersCountryCodes.length
              ? usersCountryCodes.map(({ name, countryCode }) => (
                  <span key={countryCode} className={styles.contactsPhone}>
                    {name}
                  </span>
                ))
              : '—'}
          </div>
        ),
        // An admin may have no available country at all.
        accessor: ({ usersCountryCodes }) => usersCountryCodes[0]?.name ?? '—',
        width: 4,
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status, role },
          },
        }: CellProps<IAdminUser>) => (
          <StatusCell
            id={id}
            status={status}
            changeStatus={changeAdminUserStatus}
            disabled={disabled || role === 'super-admin'}
          />
        ),
        accessor: ({ status }) => status,
        width: 3,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({ row: { original } }: CellProps<IAdminUser>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button
                  className={`${styles.editBtn} ${disabled || original.role === 'super-admin' ? styles.disabled : ''}`}
                  onClick={() => onEditClick(original)}
                  disabled={disabled || original.role === 'super-admin'}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message='edit'
              place='top'
            />
          </div>
        ),
        width: 3,
        disableSortBy: true,
      },
    ],
    // eslint-disable-next-line
    [disabled]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return (
    <>
      <div className={styles.search}>
        <input
          type='text'
          onChange={(e) => setSearch(e.currentTarget.value)}
          className={styles.searchInput}
          placeholder={t('search')}
        />
        <Button
          title={t('addBtn')}
          onClick={() => openPopUp({ title: t('addBtn'), buttonLabel: t('save'), formName: 'admin-user' })}
          stylesProp={{ padding: '8px 36px' }}
          isDisabled={disabled}
        />
      </div>
      {adminUsers && adminUsers.length > 0 ? (
        <Table
          columns={columns as never}
          data={[...filtred]}
          simpleList
          isPaginated={adminUsers.length > PAGE_SIZE}
          // isManualSorting
          // defaultSortId={usersQuery.orderBy}
          // defaultSortOrder={usersQuery.orderDirection === 'DESC'}
          // apiSort={(orderBy, orderDirection) =>
          //   (usersQuery?.orderBy !== orderBy || usersQuery?.orderDirection !== orderDirection) &&
          //   mutate({ type: usersQuery.type, orderBy, orderDirection })
          // }
          // addData={(offset) => mutate({ type: usersQuery.type, offset, limit: TABLE_LIMIT })}
        />
      ) : (
        <NoDataComponent />
      )}
    </>
  );
});

export default AdminUsersTable;
